import type { Reminder } from '../../services/occasion-repeat'

// Wizard flow — the draft task and what its answers imply. Pure data: no DOM
// and no storage here, so the ordering rules and the reminder rule below can
// be read, and tested, on their own.

export type SlideId = 'day' | 'title' | 'when' | 'reminder'

export interface Draft {
    title: string
    // 'HH:MM', or empty when the task belongs to the day as a whole.
    time: string
    reminder: Reminder
}

// A new task has no hour and no reminder until the user says otherwise.
export const emptyDraft = (): Draft => ({ title: '', time: '', reminder: 'none' })

// A task asks the same three questions every time, and one more when the caller
// did not say which day it is for — from the day page that is where you already
// are, from the month it has to be asked. It goes first when it is there at all:
// every other flow into this page starts on a day already settled, so the day
// belongs before the name rather than in the middle of the questions about it.
// No answer of the user's own adds or removes a step; the time only changes what
// the last one offers.
export const visibleSlides = (pickDay = false): SlideId[] =>
    pickDay ? ['day', 'title', 'when', 'reminder'] : ['title', 'when', 'reminder']

// Only the name can block: a task with no name has nothing to show on the day.
// The other two carry a usable default.
export const canAdvance = (id: SlideId, draft: Draft): boolean =>
    id !== 'title' || !!draft.title.trim()

// What each step asks for, in the form the button in front of it can say. The
// wording is here rather than in the step itself because it is the step before
// that has to name it — "ادامه" alone says only that there is more, which is the
// one thing the dots already say.
const ASKS: Record<SlideId, string> = {
    day: 'انتخاب روز',
    title: 'نوشتن نام',
    when: 'مشخص کردن ساعت',
    reminder: 'تنظیم یادآوری',
}

// What the way-on button says from here: where it goes, or that this is the
// end of it.
export const nextLabel = (id: SlideId, pickDay: boolean): string => {
    const next = step(id, pickDay, 1)
    return next ? `ادامه و ${ASKS[next]}` : 'ذخیره'
}

export const step = (id: SlideId, pickDay: boolean, delta: 1 | -1): SlideId | null => {
    const ids = visibleSlides(pickDay)
    const next = ids.indexOf(id) + delta
    return next >= 0 && next < ids.length ? ids[next] : null
}

// An hour before what? A task with no clock is due on its day rather than at a
// moment, so the hour lead is only offered once a time has been picked — see
// services/occurrences.ts, where a task without one counts back from nine in
// the morning.
export const reminderChoices = (draft: Draft): { value: Reminder, label: string }[] => {
    const choices: { value: Reminder, label: string }[] = [
        { value: 'none', label: 'بدون یادآوری' },
        { value: 'week', label: 'یک هفته قبل' },
        { value: 'day', label: 'یک روز قبل' },
    ]
    if (draft.time) choices.push({ value: 'hour', label: 'یک ساعت قبل' })
    return choices
}

// Going back and clearing the time takes the hour lead away with it. The user
// asked to be reminded, so it falls back to the day before rather than to
// nothing at all.
export const withoutStaleLead = (draft: Draft): Draft =>
    !draft.time && draft.reminder === 'hour' ? { ...draft, reminder: 'day' } : draft
